import React, { useState } from "react";
import { Box, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import IconButton from "@material-ui/core/IconButton";
import {
  frequentFlyerRegex as rewardsRegex,
  B2BTextField,
  NotificationBanner,
  BannerSeverity,
  GenericInfoPopup,
  Icon,
  IconName,
  useDeviceTypes,
} from "halifax";
import { RouteComponentProps } from "react-router";
import clsx from "clsx";
import {
  ADD_HERTZ_LOYALTY_REWARDS,
  CallState,
  CarsBookErrorEnum,
  ITravelerStepErrors,
  PaymentError,
} from "redmond";
import { ProductError } from "@b2bportal/purchase-api";
import { getUpdatedTraveler } from "@capone/common";
import { HertzLoyaltyFormConnectorProps } from "./container";
import { PRESIDENTS_CIRCLE } from "../../../rewards/reducer/selectors/hertzTierStatus";
import "./styles.scss";
import hertzGoldLogo from "./assets/hertz-gold-logo.png";
import {
  HERTZ_REWARDS_SUBTITLE,
  HERTZ_REWARDS_WARNING,
  HERTZ_MODAL_PRESIDENTS_CIRCLE_INTRO,
  HERTZ_MODAL_FIVE_STAR_INTRO,
  HERTZ_MODAL_PRESIDENTS_CIRCLE_BENEFITS,
  HERTZ_MODAL_FIVE_STAR_BENEFITS,
  HERTZ_MODAL_LOGO_ALT,
  HERTZ_MODAL_ENROLL_LINK,
  HERTZ_MODAL_TERMS,
  getHertzSignupUrl
} from "./constants";
import { trackEvent } from "../../../../api/v1/analytics/trackEvent";
import {
  useExperiments,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  CARS_HERTZ_LOYALTY_HIGHLIGHTS,
  CARS_HERTZ_LOYALTY_HIGHLIGHTS_VARIANTS,
  CARS_LOYALTY_SECTION,
  CONTROL,
  AVAILABLE,
  AVAILABLE_WITH_LINKS
} from "../../../../context/experiments";


const OPTIONAL_LABEL = <span className="optional-label">(Optional)</span>;

const HERTZ_LOYALTY_REWARDS_TITLE = (supplierRef?: string) => {
  switch (supplierRef) {
    case "ZE":
      return <>Add your Hertz Gold+ number {OPTIONAL_LABEL}</>;
    case "ZT":
      return <>Add your Thrifty Blue Chip number {OPTIONAL_LABEL}</>;
    case "ZR":
      return <>Add your Dollar Express number {OPTIONAL_LABEL}</>;
    default:
      return null;
  }
};

export interface IHertzLoyaltyFormProps
  extends RouteComponentProps,
    HertzLoyaltyFormConnectorProps {
  isMobile?: boolean;
  disabled?: boolean;
  showErrors?: ITravelerStepErrors;
  setShowErrors?: (showErrors: ITravelerStepErrors) => void;
}

export const HertzLoyaltyForm = ({
  hertzLoyaltyRewardsNumber,
  setHertzLoyaltyRewardsNumber,
  isMobile,
  disabled,
  showErrors,
  setShowErrors,
  priceQuoteErrors,
  confirmationDetailsErrors,
  selectedVehicle,
  useV1PurchaseFlow,
  selectedDriver,
  hertzTierStatus,
  pollPriceQuoteCallState,
  userPassengerCallState,
  updateUserPassenger,
  pendingLoyaltySync,
  setPendingLoyaltySync,
}: IHertzLoyaltyFormProps) => {
  React.useEffect(() => {
    // retrieve the supplier-specific loyalty code 
    const vendorCode = selectedVehicle?.supplierRef;
    const driverLoyaltyNumber = vendorCode ? selectedDriver?.groundLoyalty?.[vendorCode]?.value || "" : "";
    setHertzLoyaltyRewardsNumber(driverLoyaltyNumber);
  }, [selectedDriver?.id, selectedDriver?.groundLoyalty]);

  React.useEffect(() => {
    if (
      [...priceQuoteErrors, ...confirmationDetailsErrors].some((error) =>
        useV1PurchaseFlow
          ? (error as ProductError)?.value?.value?.msg ===
              CarsBookErrorEnum.InvalidLoyaltyNumber
          : (error as PaymentError)?.msg ===
              CarsBookErrorEnum.InvalidLoyaltyNumber
      )
    ) {
      setShowErrors?.({ ...showErrors, loyaltyNumber: true });
    }
  }, [priceQuoteErrors, confirmationDetailsErrors]);

  const { matchesMobile } = useDeviceTypes();
  const [isHertzInfoModalOpen, setIsHertzInfoModalOpen] = useState(false);

  const isHertzDirect =
    selectedVehicle?.supplierRef === "ZE" &&
    selectedVehicle?.vendorRef === "HE";


  const { experiments } = useExperiments();
  const hertzLoyaltyVariant = getExperimentVariantCustomVariants(
    experiments,
    CARS_HERTZ_LOYALTY_HIGHLIGHTS,
    CARS_HERTZ_LOYALTY_HIGHLIGHTS_VARIANTS,
  );
  const isCarsHertzLoyaltyHighlightsControl = hertzLoyaltyVariant === CONTROL;
  const isCarsHertzLoyaltyHighlightsAvailable = hertzLoyaltyVariant === AVAILABLE;
  const isCarsHertzLoyaltyHighlightsAvailableWithLinks = hertzLoyaltyVariant === AVAILABLE_WITH_LINKS;
  const hertzSignupUrl = hertzTierStatus != null
    ? getHertzSignupUrl(hertzTierStatus.caponeProduct)
    : null;

  const isLoyaltySectionEnabled =
    getExperimentVariant(experiments, CARS_LOYALTY_SECTION) === AVAILABLE;

  const prevPollPriceQuoteCallStateRef = React.useRef(pollPriceQuoteCallState);

  // Consumer-only sync: the corp workflow already syncs before schedulePriceQuote.
  React.useEffect(() => {
    const wasNotSuccess =
      prevPollPriceQuoteCallStateRef.current !== CallState.Success;
    const nowSuccess = pollPriceQuoteCallState === CallState.Success;
    prevPollPriceQuoteCallStateRef.current = pollPriceQuoteCallState;

    if (!wasNotSuccess || !nowSuccess || !isLoyaltySectionEnabled) return;

    const vendorCode = selectedVehicle?.supplierRef;
    const enteredNumber = (hertzLoyaltyRewardsNumber ?? "").trim();
    if (!vendorCode || !selectedDriver || !enteredNumber) return;

    const savedNumber =
      selectedDriver.groundLoyalty?.[vendorCode]?.value ?? "";
    if (enteredNumber === savedNumber) return;

    const updatedPerson = getUpdatedTraveler(
      selectedDriver,
      null,
      enteredNumber,
      undefined,
      undefined,
      vendorCode
    );

    setPendingLoyaltySync({ vendorCode, loyaltyNumber: enteredNumber });
    updateUserPassenger(
      { person: updatedPerson, source: "checkout" },
      undefined,
      "cars-checkout-loyalty-sync"
    );
  }, [pollPriceQuoteCallState]);

  // Banner visibility is derived from Redux. The corp checkout's
  // CarBookPassengerSelection unmounts this form while userPassengerCallState
  // is InProcess, so component-local state can't survive the sync transition.
  // Sourcing visibility from Redux lets the banner appear on the remount.
  const showSavedBanner =
    pendingLoyaltySync != null &&
    userPassengerCallState === CallState.Success &&
    (selectedDriver?.groundLoyalty?.[pendingLoyaltySync.vendorCode]?.value ??
      "") === pendingLoyaltySync.loyaltyNumber;

  const onRewardsBlur = (val: string) => {
    if (!val) {
      setShowErrors?.({ ...showErrors, loyaltyNumber: false });
    } else if (rewardsRegex.test(val)) {
      setShowErrors?.({ ...showErrors, loyaltyNumber: false });
    } else {
      setShowErrors?.({ ...showErrors, loyaltyNumber: true });
    }
  };

  const onRewardsChanged = (val: string) => {
    setHertzLoyaltyRewardsNumber(val);
    if (rewardsRegex.test(val)) {
      setShowErrors?.({ ...showErrors, loyaltyNumber: false });
    }
    trackEvent({
      eventName: ADD_HERTZ_LOYALTY_REWARDS,
      properties: {},
    });
  };

  const onHertzInfoClick = () => {
    setIsHertzInfoModalOpen(true);
  };

  const onHertzInfoClose = () => {
    setIsHertzInfoModalOpen(false);
  };

  return (
    <Box
      className={clsx("hertz-loyalty-rewards-container", { mobile: isMobile })}
    >
      <Box className="hertz-loyalty-rewards-description">
        {isHertzDirect && (
          <img
            src={hertzGoldLogo}
            alt={HERTZ_MODAL_LOGO_ALT}
            className="hertz-title-logo"
          />
        )}

        <Typography component="h3" variant="h4" className="title">
          {HERTZ_LOYALTY_REWARDS_TITLE(selectedVehicle?.supplierRef)}
        </Typography>
        {!isCarsHertzLoyaltyHighlightsControl && isHertzDirect && hertzTierStatus != null && (
          <Box className="dynamic-paragraph-row">
            <Typography variant="body2" className="dynamic-paragraph">
              With {hertzTierStatus.cardName}, enjoy the benefits of Hertz{" "}
                {hertzTierStatus.tier === PRESIDENTS_CIRCLE
                  ? "President's Circle® Status"
                  : "Five Star® Status"}
            </Typography>
            <IconButton
              className="dynamic-paragraph-info-icon"
              aria-label="More info about Hertz Gold Plus rewards"
              onClick={onHertzInfoClick}
              size="small"
            >
              <InfoOutlinedIcon className='hertz-info-icon' />
            </IconButton>
          </Box>
        )}
        <Typography variant="body2" className="subtitle">
          {HERTZ_REWARDS_SUBTITLE(selectedVehicle?.supplierRef)}
        </Typography>

        <NotificationBanner
          className="hertz-rewards-price-banner"
          label={HERTZ_REWARDS_WARNING(selectedVehicle?.supplierRef)}
          severity={BannerSeverity.NOTICE}
        />

      </Box>
      <B2BTextField
        className="hertz-rewards-input-field"
        label="Loyalty number"
        value={hertzLoyaltyRewardsNumber}
        onChange={(value) => onRewardsChanged(value)}
        type="text"
        id="hertz-rewards-input-field"
        errorHelper={
          showErrors?.loyaltyNumber
            ? "Enter a valid loyalty number."
            : undefined
        }
        onBlur={(value) => onRewardsBlur(value)}
        disabled={disabled}
        shrink={hertzLoyaltyRewardsNumber ? true : undefined}
      />

      {showSavedBanner && (
        <NotificationBanner
          className="hertz-loyalty-saved-banner"
          severity={BannerSeverity.IMPROVEMENT}
          content={
            <Box className="hertz-loyalty-saved-banner-content">
              <Icon name={IconName.CheckCircleFilledGreen} />
              <Typography variant="body2" className="label hertz-loyalty-saved-banner-text">
                Your loyalty number has been saved to your traveler profile and will be applied to this booking.
              </Typography>
              <IconButton
                aria-label="Dismiss confirmation"
                onClick={() => setPendingLoyaltySync(null)}
                size="small"
                className="hertz-loyalty-saved-banner-close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          }
        />
      )}

      {!isCarsHertzLoyaltyHighlightsControl && <GenericInfoPopup
        className="hertz-info-popup"
        open={isHertzInfoModalOpen}
        isMobile={matchesMobile}
        onClose={onHertzInfoClose}
        subtitle={
          <Box>
            {/* Mobile: title + divider */}
            {matchesMobile && (
              <Box className="hertz-modal-header-mobile">
                <Typography variant="h6" className="hertz-modal-title">
                  Hertz Gold+ Program
                </Typography>
                <IconButton
                  className="hertz-modal-close-button"
                  aria-label="Close"
                  onClick={onHertzInfoClose}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            )}

            {!matchesMobile && (
              <Box className="hertz-modal-header-desktop">
                <img
                  src={hertzGoldLogo}
                  alt={HERTZ_MODAL_LOGO_ALT}
                  className="hertz-modal-logo"
                />
                <IconButton
                  className="hertz-modal-close-button"
                  aria-label="Close"
                  onClick={onHertzInfoClose}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            )}

            {/* Logo */}
            {matchesMobile && (
              <img
                src={hertzGoldLogo}
                alt={HERTZ_MODAL_LOGO_ALT}
                className="hertz-modal-logo hertz-modal-logo--mobile"
              />
            )}
            

            {/* Intro + benefits */}
            {hertzTierStatus != null && (
              <>
                <Typography variant="body2" className="hertz-modal-text">
                  {hertzTierStatus.tier === PRESIDENTS_CIRCLE
                    ? HERTZ_MODAL_PRESIDENTS_CIRCLE_INTRO(hertzTierStatus.cardName)
                    : HERTZ_MODAL_FIVE_STAR_INTRO(hertzTierStatus.cardName)}
                </Typography>
                <ul className="hertz-modal-benefits">
                  {(hertzTierStatus.tier === PRESIDENTS_CIRCLE
                    ? HERTZ_MODAL_PRESIDENTS_CIRCLE_BENEFITS
                    : HERTZ_MODAL_FIVE_STAR_BENEFITS
                  ).map((benefit) => (
                    <li key={benefit}>
                      <Typography variant="body2" component="span" className="hertz-modal-text">
                        {benefit}
                      </Typography>
                    </li>
                  ))}
                </ul>
                <Typography
                  variant="caption"
                  component="p"
                  className="hertz-modal-terms"
                >
                  <em>{HERTZ_MODAL_TERMS}</em>
                </Typography>
              </>
            )}

            {isCarsHertzLoyaltyHighlightsAvailableWithLinks && hertzSignupUrl != null && (
              <a
                href={hertzSignupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hertz-modal-enroll-link"
              >
                {HERTZ_MODAL_ENROLL_LINK}
              </a>
            )}
            {isCarsHertzLoyaltyHighlightsAvailable && (
              <Box className="hertz-modal-enroll-text">
                <Typography variant="body2">
                  To enroll, sign into your Capital One account or mobile app:
                </Typography>
                <ul>
                  <li>
                    <Typography variant="body2" component="span">
                      Navigate to the <strong>&apos;Explore rewards and benefits&apos;</strong> tile and <strong>&apos;Benefits&apos;</strong> tab to find the Hertz benefit
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" component="span">
                      Then select <strong>&apos;Enroll now&apos;</strong> to complete your membership on the Hertz enrollment page.
                    </Typography>
                  </li>
                </ul>
              </Box>
            )}
          </Box>
        }
      />}
    </Box>
  );
};
